import { EventBus, GameEventTypes } from '@antification/core';

/**
 * Parameter definition for the settings panel.
 */
export interface ParamDef {
  key: string;           // e.g. "parkington.cars.MOVE_SPEED"
  label: string;         // e.g. "Car Speed"
  description?: string;
  helpText?: string;     // Detailed help shown in help dialog
  relatedSettings?: string[];  // Keys of related settings to cross-reference
  defaultValue: number;
  min: number;
  max: number;
  step: number;
  type: 'integer' | 'float' | 'duration' | 'ratio';
  unit?: string;         // "ms", "cells", "%"
  enumLabels?: string[]; // Named options for integer params (index = value)
  restartRequired?: boolean;
  category: string;      // "parkington.cars"
}

/**
 * Category metadata for the settings panel accordion.
 */
export interface CategoryDef {
  key: string;           // e.g. "parkington.cars"
  label: string;         // e.g. "Cars"
  parent?: string;       // e.g. "parkington" for nesting
  debugOnly?: boolean;   // only show when ?debug is in URL
}

/**
 * Exported settings shape for game save.
 */
export interface ExportedSettings {
  parkington: Record<string, number>;
  colonies: Array<Record<string, number>>;
}

/**
 * Central store for all game parameter definitions, current values, and defaults.
 * Emits CONFIG_CHANGED events via EventBus when values change.
 * Settings persist only via game save/load, not independently in localStorage.
 *
 * Formicarium keys are per-colony: get/set operate on the active colony's values.
 * Parkington keys remain global.
 */
export class ConfigRegistry {
  private static instance: ConfigRegistry;

  private params: Map<string, ParamDef> = new Map();
  private values: Map<string, number> = new Map();
  private categories: Map<string, CategoryDef> = new Map();
  private events: EventBus;

  /** Per-colony formicarium settings. Colony index -> (key -> value). */
  private colonyValues: Map<number, Map<string, number>> = new Map();
  private activeColony = 0;

  private constructor() {
    this.events = EventBus.getInstance();
  }

  static getInstance(): ConfigRegistry {
    if (!ConfigRegistry.instance) {
      ConfigRegistry.instance = new ConfigRegistry();
    }
    return ConfigRegistry.instance;
  }

  private isFormicariumKey(key: string): boolean {
    return key.startsWith('formicarium.');
  }

  /**
   * Initialize per-colony storage for the given number of colonies.
   */
  initColonies(count: number): void {
    for (let i = 0; i < count; i++) {
      if (!this.colonyValues.has(i)) {
        this.colonyValues.set(i, new Map());
      }
    }
  }

  /**
   * Set the active colony index for formicarium get/set operations.
   */
  setActiveColony(index: number): void {
    this.activeColony = index;
  }

  getActiveColony(): number {
    return this.activeColony;
  }

  /**
   * Register a category for the accordion UI.
   */
  registerCategory(def: CategoryDef): void {
    this.categories.set(def.key, def);
  }

  /**
   * Register a parameter definition.
   * Sets current value to default if not already loaded from storage.
   */
  register(def: ParamDef): void {
    this.params.set(def.key, def);
    // Only store non-formicarium keys in the global values map
    if (!this.isFormicariumKey(def.key)) {
      if (!this.values.has(def.key)) {
        this.values.set(def.key, def.defaultValue);
      }
    }
  }

  /**
   * Get the current value of a parameter.
   * For formicarium keys, returns the active colony's value (falling back to default).
   */
  get(key: string): number {
    if (this.isFormicariumKey(key)) {
      const colonyMap = this.colonyValues.get(this.activeColony);
      if (colonyMap?.has(key)) {
        return colonyMap.get(key)!;
      }
      return this.params.get(key)?.defaultValue ?? 0;
    }
    return this.values.get(key) ?? this.params.get(key)?.defaultValue ?? 0;
  }

  /**
   * Set a parameter value and emit CONFIG_CHANGED.
   * For formicarium keys, stores in the active colony's map.
   */
  set(key: string, value: number): void {
    const def = this.params.get(key);
    if (!def) return;

    // Clamp to range
    value = Math.max(def.min, Math.min(def.max, value));

    // Snap to step
    if (def.type === 'integer') {
      value = Math.round(value);
    } else {
      // Round to step precision
      const precision = this.getStepPrecision(def.step);
      value = parseFloat(value.toFixed(precision));
    }

    if (this.isFormicariumKey(key)) {
      let colonyMap = this.colonyValues.get(this.activeColony);
      if (!colonyMap) {
        colonyMap = new Map();
        this.colonyValues.set(this.activeColony, colonyMap);
      }
      const oldValue = colonyMap.get(key) ?? def.defaultValue;
      if (oldValue === value) return;

      // If resetting to default, remove the entry to keep the map clean
      if (value === def.defaultValue) {
        colonyMap.delete(key);
      } else {
        colonyMap.set(key, value);
      }
      this.events.emit(GameEventTypes.CONFIG_CHANGED, { key, value, oldValue });
    } else {
      const oldValue = this.values.get(key);
      if (oldValue === value) return;

      this.values.set(key, value);
      this.events.emit(GameEventTypes.CONFIG_CHANGED, { key, value, oldValue });
    }
  }

  /**
   * Get all parameter definitions in a category.
   */
  getCategory(prefix: string): ParamDef[] {
    const result: ParamDef[] = [];
    for (const [key, def] of this.params) {
      if (def.category === prefix) {
        result.push(def);
      }
    }
    return result;
  }

  /**
   * Get all registered categories sorted by key.
   */
  getAllCategories(): CategoryDef[] {
    return Array.from(this.categories.values()).sort((a, b) => a.key.localeCompare(b.key));
  }

  /**
   * Get all parameter definitions.
   */
  getAllParams(): ParamDef[] {
    return Array.from(this.params.values());
  }

  /**
   * Reset all params in a category to defaults.
   * For formicarium categories, resets the active colony's values only.
   */
  resetCategory(prefix: string): void {
    for (const [key, def] of this.params) {
      if (def.category === prefix) {
        this.set(key, def.defaultValue);
      }
    }
  }

  /**
   * Reset ALL params to defaults.
   * Resets global parkington values and the active colony's formicarium values.
   */
  resetAll(): void {
    for (const [key, def] of this.params) {
      this.set(key, def.defaultValue);
    }
  }

  /**
   * Check if a category has any non-default values.
   * For formicarium categories, checks the active colony.
   */
  categoryHasChanges(prefix: string): boolean {
    for (const [key, def] of this.params) {
      if (def.category === prefix) {
        const current = this.get(key);
        if (current !== def.defaultValue) return true;
      }
    }
    return false;
  }

  /**
   * Check if any param has a non-default value.
   */
  hasAnyChanges(): boolean {
    for (const [key, def] of this.params) {
      const current = this.get(key);
      if (current !== def.defaultValue) return true;
    }
    return false;
  }

  /**
   * Check if any param under a root category prefix has a non-default value.
   * For formicarium, checks the active colony.
   */
  hasAnyChangesUnder(rootPrefix: string): boolean {
    for (const [key, def] of this.params) {
      if (def.category.startsWith(rootPrefix)) {
        const current = this.get(key);
        if (current !== def.defaultValue) return true;
      }
    }
    return false;
  }

  /**
   * Reset all params under a root category prefix to defaults.
   * For formicarium, resets the active colony only.
   */
  resetAllUnder(rootPrefix: string): void {
    for (const [key, def] of this.params) {
      if (def.category.startsWith(rootPrefix)) {
        this.set(key, def.defaultValue);
      }
    }
  }

  /**
   * Export all non-default settings for game save.
   * Returns per-colony formicarium settings and global parkington settings.
   */
  exportSettings(): ExportedSettings {
    const parkington: Record<string, number> = {};
    for (const [key, def] of this.params) {
      if (!this.isFormicariumKey(key)) {
        const current = this.values.get(key) ?? def.defaultValue;
        if (current !== def.defaultValue) {
          parkington[key] = current;
        }
      }
    }

    const colonies: Array<Record<string, number>> = [];
    for (let i = 0; i < Math.max(1, this.colonyValues.size); i++) {
      const colonyMap = this.colonyValues.get(i);
      const colonyData: Record<string, number> = {};
      if (colonyMap) {
        for (const [key, value] of colonyMap) {
          colonyData[key] = value;
        }
      }
      colonies.push(colonyData);
    }

    return { parkington, colonies };
  }

  /**
   * Import settings from game load.
   * Accepts both new per-colony format and old flat format for backwards compatibility.
   * Does not emit CONFIG_CHANGED events during import (to avoid routing issues with per-colony settings).
   * Instead emits a single SETTINGS_IMPORTED event when done, and Game.ts applies settings to colonies.
   */
  importSettings(data: Record<string, number> | ExportedSettings): void {
    // Reset all global values to defaults (silently)
    for (const [key, def] of this.params) {
      if (!this.isFormicariumKey(key)) {
        this.values.set(key, def.defaultValue);
      }
    }

    // Reset all colony values
    for (const [, colonyMap] of this.colonyValues) {
      colonyMap.clear();
    }

    if (this.isNewFormat(data)) {
      // New per-colony format
      const exported = data as ExportedSettings;

      // Apply parkington settings (silently)
      for (const [key, value] of Object.entries(exported.parkington)) {
        if (typeof value === 'number' && this.params.has(key)) {
          this.setSilent(key, value);
        }
      }

      // Apply per-colony formicarium settings (silently)
      const savedActive = this.activeColony;
      for (let i = 0; i < exported.colonies.length; i++) {
        this.activeColony = i;
        for (const [key, value] of Object.entries(exported.colonies[i])) {
          if (typeof value === 'number' && this.params.has(key)) {
            this.setSilent(key, value);
          }
        }
      }
      this.activeColony = savedActive;
    } else {
      // Old flat format - split by prefix; formicarium keys apply to all colonies
      const flat = data as Record<string, number>;
      const savedActive = this.activeColony;

      for (const [key, value] of Object.entries(flat)) {
        if (typeof value !== 'number' || !this.params.has(key)) continue;

        if (this.isFormicariumKey(key)) {
          // Apply to all colonies
          for (const [colonyIdx] of this.colonyValues) {
            this.activeColony = colonyIdx;
            this.setSilent(key, value);
          }
        } else {
          this.setSilent(key, value);
        }
      }
      this.activeColony = savedActive;
    }

    // Emit a single event so Game.ts can apply all settings to all systems
    this.events.emit(GameEventTypes.SETTINGS_IMPORTED, {});
  }

  /**
   * Set a parameter value without emitting CONFIG_CHANGED.
   * Used during import to avoid event-routing issues.
   */
  private setSilent(key: string, value: number): void {
    const def = this.params.get(key);
    if (!def) return;

    // Clamp to range
    value = Math.max(def.min, Math.min(def.max, value));

    // Snap to step
    if (def.type === 'integer') {
      value = Math.round(value);
    } else {
      const precision = this.getStepPrecision(def.step);
      value = parseFloat(value.toFixed(precision));
    }

    if (this.isFormicariumKey(key)) {
      let colonyMap = this.colonyValues.get(this.activeColony);
      if (!colonyMap) {
        colonyMap = new Map();
        this.colonyValues.set(this.activeColony, colonyMap);
      }
      if (value === def.defaultValue) {
        colonyMap.delete(key);
      } else {
        colonyMap.set(key, value);
      }
    } else {
      this.values.set(key, value);
    }
  }

  private isNewFormat(data: unknown): data is ExportedSettings {
    return typeof data === 'object' && data !== null && 'parkington' in data && 'colonies' in data;
  }

  /**
   * Get decimal precision needed for a step size.
   */
  private getStepPrecision(step: number): number {
    const str = step.toString();
    const dot = str.indexOf('.');
    return dot >= 0 ? str.length - dot - 1 : 0;
  }

  /**
   * Reset singleton (for testing).
   */
  static reset(): void {
    if (ConfigRegistry.instance) {
      ConfigRegistry.instance.params.clear();
      ConfigRegistry.instance.values.clear();
      ConfigRegistry.instance.categories.clear();
      ConfigRegistry.instance.colonyValues.clear();
    }
  }
}
